import Image from 'next/image';
import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Color,
  Display,
  Display1,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Heading5,
  Icon,
  IconVariant,
  ResponsiveValue,
  Space,
  Strong,
  Text,
  TextAlign,
  useTheme,
  withCitrusTheme,
} from '@gousto-internal/citrus-react';
import { CSSObject } from '@emotion/react';
import { ButtonLink } from '../ButtonLink/ButtonLink';
import { type DiscountStep, formatCompactDiscountStepLabel } from '../../utils/discountSteps';
import OfferEuro from '../icons/OfferEuro';
import React from 'react';
import { RAF_PULSE_GOLD } from '../FreeFood/constants';

const desktopBackgroundColor = '#333d47';
const textShadow = '0 2px 4px rgba(0, 0, 0, 0.45)';

type OfferStepData = {
  discount: string;
  label: string;
  isHighlighted: boolean;
};

type HeroImageConfig = {
  mobileSrc: string;
  desktopSrc: string;
  width: ResponsiveValue<string>;
  height: ResponsiveValue<string>;
  top?: ResponsiveValue<string>;
  right?: ResponsiveValue<string>;
  bottom?: ResponsiveValue<string>;
  left?: ResponsiveValue<string>;
  transform?: string;
};

const heroImages: HeroImageConfig[] = [
  {
    mobileSrc: '/assets/homepage/hero-section-stepped/hero/mobile/prawn-noodles-mobile.png',
    desktopSrc: '/assets/homepage/hero-section-stepped/hero/desktop/prawn-noodles-new.png',
    width: ['252px', '272px', null, '532px'],
    height: ['252px', '272px', null, '532px'],
    top: ['-62px', '-86px', null, '-126px'],
    left: ['calc(50% - 158px)', 'calc(50% - 178px)', null, 'calc(50% - 398px)'],
    transform: 'translateX(-50%)',
  },
  {
    mobileSrc: '/assets/homepage/hero-section-stepped/hero/mobile/katsu-mobile.png',
    desktopSrc: '/assets/homepage/hero-section-stepped/hero/desktop/katsu-new.png',
    width: ['228px', '272px', null, '430px'],
    height: ['228px', '272px', null, '430px'],
    top: ['-8px', '-108px', null, '-156px'],
    right: ['calc(50% - 210px)', 'calc(50% - 200px)', null, 'calc(50% - 390px)'],
    transform: 'translateX(50%)',
  },
  {
    mobileSrc: '/assets/homepage/hero-section-stepped/hero/mobile/salmon-tacos-mobile.png',
    desktopSrc: '/assets/homepage/hero-section-stepped/hero/desktop/salmon-tacos-new.png',
    width: ['340px', '354px', null, '640px'],
    height: ['340px', '354px', null, '640px'],
    bottom: ['114px', '-108px', null, '-166px'],
    right: ['calc(50% - 204px)', 'calc(50% - 242px)', null, 'calc(50% - 462px)'],
    transform: 'translateX(50%)',
  },
  {
    mobileSrc: '/assets/homepage/hero-section-stepped/hero/mobile/salad-mobile.png',
    desktopSrc: '/assets/homepage/hero-section-stepped/hero/desktop/salad-new.png',
    width: ['286px', '272px', null, '526px'],
    height: ['286px', '272px', null, '526px'],
    bottom: ['106px', '-58px', null, '-184px'],
    left: ['calc(50% - 204px)', 'calc(50% - 200px)', null, 'calc(50% - 486px)'],
    transform: 'translateX(-50%)',
  },
  {
    mobileSrc: '/assets/homepage/hero-section-stepped/hero/desktop/bottom-bowl.png',
    desktopSrc: '/assets/homepage/hero-section-stepped/hero/desktop/bottom-bowl.png',
    width: ['0px', '138px', null, '206px'],
    height: ['0px', '138px', null, '206px'],
    bottom: ['-406px', '-106px', null, '-105px'],
    right: ['calc(50% - 304px)', 'calc(50% - 120px)', null, 'calc(50% - 236px)'],
    transform: 'translateX(-50%)',
  },
];

const SectionContainer = withCitrusTheme(
  Box,
  () =>
    ({
      overflow: 'hidden',
      backgroundColor: desktopBackgroundColor,
    }) as CSSObject,
);

const PatternContainer = withCitrusTheme(
  Box,
  () =>
    ({
      zIndex: 0,
      transform: 'translateX(-50%) rotate(2deg)',
    }) as CSSObject,
);

const HeroImageContainer = withCitrusTheme(
  Box,
  () =>
    ({
      zIndex: 1,
      filter: 'drop-shadow(4px 0 2px rgba(0, 0, 0, 0.25))',
    }) as CSSObject,
);

const MobileHeroImage = withCitrusTheme(Box, () => ({
  display: Display.Block,
  width: '100%',
  height: '100%',
  position: DisplayPosition.Relative,
}));

const DesktopHeroImage = withCitrusTheme(Box, () => ({
  width: '100%',
  height: '100%',
  position: DisplayPosition.Relative,
}));

const ContentContainer = withCitrusTheme(
  Box,
  () =>
    ({
      marginInline: 'auto',
      zIndex: 2,
    }) as CSSObject,
);

const OfferStepContainer = withCitrusTheme(
  'li',
  () =>
    ({
      listStyle: 'none',
      flex: '1 1 0',
      margin: 0,
      padding: 0,
    }) as CSSObject,
);

const OfferStepsList = withCitrusTheme(
  'ul',
  () =>
    ({
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      listStyle: 'none',
      margin: 0,
      padding: 0,
    }) as CSSObject,
);

const CenteredBlock = withCitrusTheme(
  Box,
  () =>
    ({
      marginInline: 'auto',
    }) as CSSObject,
);

const StyledMobileDisplay1 = withCitrusTheme(Display1, () => ({
  textShadow,
  textTransform: 'uppercase',
  width: '100%',
  display: 'block',
  fontSize: '40px',
  lineHeight: '44px',
  letterSpacing: '-0.03em',
}));

const StyledDesktopDisplay1 = withCitrusTheme(Display1, () => ({
  textShadow,
  textTransform: 'uppercase',
  width: '100%',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0 8px',
  fontSize: '40px',
  lineHeight: '44px',
}));

const StyledMobileHeading5 = withCitrusTheme(
  Heading5,
  () =>
    ({
      textShadow,
      fontSize: '16px',
      lineHeight: '20px',
    }) as CSSObject,
);

const OfferCardBase = withCitrusTheme(
  Box,
  () =>
    ({
      borderRadius: '8px',
      backgroundColor: 'rgba(0, 0, 0, 0.16)',
      backdropFilter: 'blur(8px)',
    }) as CSSObject,
);

const OfferCardPulse = withCitrusTheme(
  Box,
  () =>
    ({
      borderRadius: '8px',
      backgroundColor: RAF_PULSE_GOLD,
    }) as CSSObject,
);

function OfferCard({
  pulse,
  ...props
}: React.ComponentProps<typeof OfferCardBase> & { pulse: boolean }) {
  const Component = pulse ? OfferCardPulse : OfferCardBase;
  return <Component {...props} />;
}

const OfferStepCard = withCitrusTheme(
  Box,
  () =>
    ({
      borderRadius: '4px',
      minHeight: '52px',
    }) as CSSObject,
);

const StyledAsterisk = withCitrusTheme(
  'span',
  () =>
    ({
      verticalAlign: 'super',
      fontSize: '24px',
      lineHeight: '24px',
    }) as CSSObject,
);

const PricePill = withCitrusTheme(
  Box,
  () =>
    ({
      borderRadius: '100px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }) as CSSObject,
);

type BackgroundPatternConfig = {
  src: string;
  display: ResponsiveValue<Display>;
  width: string;
  height: string;
  top: string;
  sizes: string;
  opacity: number;
};

const backgroundPatterns: BackgroundPatternConfig[] = [
  {
    src: '/assets/homepage/hero-section-stepped/background/mobile-pattern.svg',
    display: [Display.Block, null, Display.None],
    width: '436px',
    height: '376px',
    top: '-82px',
    sizes: '246px',
    opacity: 0.75,
  },
  {
    src: '/assets/homepage/hero-section-stepped/background/desktop-pattern.svg',
    display: [Display.None, null, Display.Block],
    width: '545px',
    height: '469px',
    top: '-112px',
    sizes: '545px',
    opacity: 0.86,
  },
];

function toOfferSteps(discountSteps: DiscountStep[]): OfferStepData[] {
  return discountSteps.map((step, index) => ({
    discount: `${step.percentage} OFF`,
    label: formatCompactDiscountStepLabel(step),
    isHighlighted: index === 0,
  }));
}

function BackgroundPattern({
  src,
  display,
  width,
  height,
  top,
  sizes,
  opacity,
}: BackgroundPatternConfig) {
  return (
    <PatternContainer
      display={display}
      position={DisplayPosition.Absolute}
      top={top}
      left="50%"
      width={width}
      height={height}
      aria-hidden
    >
      <Image src={src} alt="" fill sizes={sizes} style={{ objectFit: 'contain', opacity }} />
    </PatternContainer>
  );
}

function OfferStep({
  discount,
  label,
  isHighlighted,
  pulseStyling,
}: {
  discount: string;
  label: string;
  isHighlighted: boolean;
  pulseStyling?: boolean;
}) {
  const [value, offLabel] = discount.split(' ');

  const stepCardBg = pulseStyling
    ? Color.ColdGrey_900
    : isHighlighted
      ? Color.Error_500
      : Color.ColdGrey_500;

  return (
    <OfferStepContainer>
      <OfferStepCard bg={stepCardBg} paddingV={1} paddingH={2}>
        <Text
          size={5}
          color={Color.White}
          fontWeight={FontWeight.Bold}
          textAlign={TextAlign.Center}
        >
          <Box display={Display.Block}>{value}</Box>
          <Box display={Display.Block}>{offLabel}</Box>
        </Text>
      </OfferStepCard>
      <Space size={1} />
      <Body3 color={pulseStyling ? Color.ColdGrey_900 : Color.White} textAlign={TextAlign.Center}>
        {label}
      </Body3>
    </OfferStepContainer>
  );
}

function HeroImage({
  mobileSrc,
  desktopSrc,
  width,
  height,
  top,
  right,
  bottom,
  left,
  transform,
}: HeroImageConfig) {
  const { theme } = useTheme();
  const desktopBreakpoint = theme.tokens.breakpoints[1];
  const sizes = `(min-width: ${desktopBreakpoint}) 33vw, 50vw`;

  return (
    <HeroImageContainer
      position={DisplayPosition.Absolute}
      width={width}
      height={height}
      top={top}
      right={right}
      bottom={bottom}
      left={left}
      style={{ transform }}
      aria-hidden="true"
    >
      <MobileHeroImage display={[Display.Block, null, Display.None]}>
        <Image src={mobileSrc} alt="" fill sizes={sizes} style={{ objectFit: 'contain' }} />
      </MobileHeroImage>
      <DesktopHeroImage display={[Display.None, null, Display.Block]}>
        <Image src={desktopSrc} alt="" fill sizes={sizes} style={{ objectFit: 'contain' }} />
      </DesktopHeroImage>
    </HeroImageContainer>
  );
}

type SteppedHeroVariant = 'homepage' | 'raf';

type HeroCopy = {
  mobileHeading: React.ReactNode;
  desktopHeading: React.ReactNode;
  subheading: (recipesAvailableThisMonth: number | null) => React.ReactNode;
  offerLabel: React.ReactNode;
  pulseOfferLabel?: React.ReactNode;
  showOfferBenefit: boolean;
};

const heroCopyByVariant: Record<SteppedHeroVariant, HeroCopy> = {
  homepage: {
    mobileHeading: (
      <>
        Ireland&apos;s
        <br />
        best value
        <br />
        recipe box
        <StyledAsterisk>*</StyledAsterisk>
      </>
    ),
    desktopHeading: (
      <>
        Ireland&apos;s best
        <br />
        value recipe box
        <StyledAsterisk>*</StyledAsterisk>
      </>
    ),
    subheading: (recipesAvailableThisMonth) => (
      <>
        Discover our biggest menu ever
        <br />
        with {recipesAvailableThisMonth !== null ? `${recipesAvailableThisMonth}+ ` : ''}recipes
        every month
      </>
    ),
    offerLabel: (
      <>
        Welcome offer<sup>‡</sup>
      </>
    ),
    showOfferBenefit: true,
  },
  raf: {
    mobileHeading: 'Your friend must like you a lot!',
    desktopHeading: (
      <>
        Your friend
        <br />
        must like you
        <br />a lot!
      </>
    ),
    subheading: () => 'Over a million people have signed up for Gousto thanks to their mates',
    offerLabel: (
      <>
        Your referral offer<sup>‡</sup>
      </>
    ),
    pulseOfferLabel: (
      <>
        Limited time referral offer<sup>‡</sup>
      </>
    ),
    showOfferBenefit: true,
  },
};

const sectionLabelByVariant: Record<SteppedHeroVariant, string> = {
  homepage: "Ireland's best value recipe box",
  raf: 'Your referral offer',
};

export type SteppedHeroMarketingContent = {
  priceDropPercentage: number;
  recipesAvailableThisMonth: number;
};

export const SteppedHeroSection = React.forwardRef<
  HTMLElement | null,
  {
    discountSteps: DiscountStep[];
    marketingContent: SteppedHeroMarketingContent | null;
    ctaHref: string;
    ctaOnClick?: () => void;
    variant: SteppedHeroVariant;
    pulseStyling?: boolean;
  }
>(function SteppedHeroSection(
  { discountSteps, marketingContent, ctaHref, ctaOnClick, variant, pulseStyling = false },
  ref,
) {
  const offerSteps = toOfferSteps(discountSteps);
  const copy = heroCopyByVariant[variant];
  const showPricePill = variant === 'homepage' && marketingContent !== null;

  return (
    <section ref={ref} aria-label={sectionLabelByVariant[variant]}>
      <SectionContainer
        bg={Color.ColdGrey_900}
        color={Color.White}
        paddingH={[2, 4, 0]}
        paddingV={[0, 6, 0]}
        minHeight={[null, '800px', '650px']}
        paddingBottom={[12, null, null]}
        position={DisplayPosition.Relative}
      >
        {backgroundPatterns.map((p) => (
          <BackgroundPattern key={p.src} {...p} />
        ))}

        {heroImages.map((image) => (
          <HeroImage key={`${image.mobileSrc}-${image.desktopSrc}`} {...image} />
        ))}

        <ContentContainer
          position={DisplayPosition.Relative}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
          maxWidth={['340px', '380px', '442px']}
          width="100%"
          paddingH={[1, 0, 0]}
        >
          <Space size={12} />
          <Space size={6} />

          <Box display={[Display.Block, null, Display.None]} width="100%">
            <CenteredBlock maxWidth="356px">
              <StyledMobileDisplay1 color={Color.White} textAlign={TextAlign.Center}>
                {copy.mobileHeading}
              </StyledMobileDisplay1>
            </CenteredBlock>
            <Space size={2} />
            <CenteredBlock maxWidth="336px">
              <StyledMobileHeading5
                color={Color.White}
                fontWeight={FontWeight.Bold}
                textAlign={TextAlign.Center}
              >
                {copy.subheading(marketingContent?.recipesAvailableThisMonth ?? null)}
              </StyledMobileHeading5>
            </CenteredBlock>
          </Box>
          <Box display={[Display.None, null, Display.Block]} width="100%">
            <CenteredBlock maxWidth="462px">
              <StyledDesktopDisplay1 color={Color.White} textAlign={TextAlign.Center}>
                {copy.desktopHeading}
              </StyledDesktopDisplay1>
            </CenteredBlock>
            <Space size={2} />
            <CenteredBlock maxWidth="442px">
              <Heading5
                color={Color.White}
                fontWeight={FontWeight.Bold}
                textAlign={TextAlign.Center}
              >
                {copy.subheading(marketingContent?.recipesAvailableThisMonth ?? null)}
              </Heading5>
            </CenteredBlock>
          </Box>

          {showPricePill && marketingContent !== null ? (
            <>
              <Space size={3} />
              <PricePill bg={Color.White} paddingV={1} paddingH={4}>
                <Body3 color={Color.ColdGrey_800} fontWeight={FontWeight.Bold}>
                  Now up to {marketingContent.priceDropPercentage}% lower prices
                </Body3>
              </PricePill>
              <Space size={4} />
            </>
          ) : (
            <Space size={5} />
          )}
          <OfferCard
            pulse={pulseStyling}
            width="100%"
            maxWidth={['334px', '372px', '442px']}
            paddingV={4}
            paddingH={4}
          >
            <Body2
              color={pulseStyling ? Color.ColdGrey_900 : Color.White}
              fontWeight={FontWeight.Bold}
              textAlign={TextAlign.Center}
            >
              {(pulseStyling && copy.pulseOfferLabel) || copy.offerLabel}
            </Body2>
            <Space size={3} />
            <OfferStepsList aria-label="Welcome offer steps">
              {offerSteps.map((step) => (
                <OfferStep key={step.label} {...step} pulseStyling={pulseStyling} />
              ))}
            </OfferStepsList>
          </OfferCard>

          <Space size={4} />
          <ButtonLink href={ctaHref} onClick={ctaOnClick} width={['334px', '320px', '343px']}>
            Get started
          </ButtonLink>

          <Space size={4} />
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.FlexStart}
            gap={1}
            width={['100%', '318px']}
            maxWidth="318px"
            color={Color.White}
          >
            <Box
              display={Display.Flex}
              alignItems={AlignItems.Center}
              gap={2}
              data-testid="stepped-hero-benefit-delivery"
            >
              <Icon name="delivery" variant={IconVariant.Inherit} aria-hidden="true" />
              <Body3 color={Color.White}>
                <Strong>FREE delivery</Strong> on your first box
              </Body3>
            </Box>
            <Box
              display={Display.Flex}
              alignItems={AlignItems.Center}
              gap={2}
              data-testid="stepped-hero-benefit-flexibility"
            >
              <Icon name="play_pause" variant={IconVariant.Inherit} aria-hidden="true" />
              <Body3 color={Color.White}>
                <Strong>No lock in:</Strong> skip or cancel for free anytime
              </Body3>
            </Box>
            {copy.showOfferBenefit && (
              <Box
                display={Display.Flex}
                alignItems={AlignItems.Center}
                gap={2}
                data-testid="stepped-hero-benefit-offer"
              >
                <OfferEuro aria-hidden="true" />
                <Body3 color={Color.White}>
                  <Strong>Save on every box</Strong> for 60 days
                </Body3>
              </Box>
            )}
            {variant === 'raf' ? <Space size={[null, null, 10]} /> : null}
          </Box>
        </ContentContainer>
      </SectionContainer>
    </section>
  );
});
